import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import CurrencyInput from '../../../../helpers/CurrencyInput';
import { getToken } from '../../../../helpers/Jwt';

const EntryPrice = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Get 'id' from endpoint params

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [initialValues, setInitialValues] = useState({
        id_unit: '',
        harga_harian: '',
        harga_mingguan: '',
        harga_bulanan: '',
        is_tersedia: 'tersedia' // default "tersedia"
    });


    const [units, setUnits] = useState([]);
    
    // Fetch list unit untuk dropdown
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await axios.get(`${BASE_URL}api/rentcar/dashboard/price/unit`);
                setUnits(response.data);
            } catch (error) {
                console.error('Gagal mengambil data unit:', error);
            }
        };
        fetchUnits();
    }, [BASE_URL]);

    // Jika id ada, fetch data lama untuk edit
    useEffect(() => {
        if (id) {
            const fetchData = async () => {
                try {
                    const response = await axios.get(`${BASE_URL}api/rentcar/dashboard/price/detail?id=${id}`, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${getToken()}`
                        }
                    });
                    const { id_unit, harga_harian, harga_mingguan, harga_bulanan, is_tersedia } = response.data;
                    setInitialValues({
                        id_unit: id_unit || '',
                        harga_harian: harga_harian || '',
                        harga_mingguan: harga_mingguan || '',
                        harga_bulanan: harga_bulanan || '',
                        is_tersedia: is_tersedia ?? 'tersedia'
                    });
                } catch (error) {
                    console.error('Gagal mengambil data:', error);
                }
            };
            fetchData();
        }
    }, [id, BASE_URL]);

    const validationSchema = Yup.object().shape({
        id_unit: Yup.string().required('Unit wajib dipilih'),
        harga_harian: Yup.number().required('Harga harian wajib diisi'),
        harga_mingguan: Yup.number().required('Harga mingguan wajib diisi'),
        harga_bulanan: Yup.number().required('Harga bulanan wajib diisi'),
        is_tersedia: Yup.string()
            .oneOf(['tersedia', 'tidak tersedia'], 'Pilih status ketersediaan')
            .required('Status ketersediaan wajib diisi')

    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const formData = new FormData();
            formData.append('id', id || '');
            formData.append('id_unit', values.id_unit);
            formData.append('harga_harian', values.harga_harian);
            formData.append('harga_mingguan', values.harga_mingguan);
            formData.append('harga_bulanan', values.harga_bulanan);
            formData.append('is_tersedia', values.is_tersedia); // langsung string "tersedia" / "tidak tersedia"

            await axios.post(`${BASE_URL}api/rentcar/dashboard/price`, formData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                }
            });

            navigate(-1);
            resetForm();
        } catch (error) {
            console.error('Gagal menyimpan data:', error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, isSubmitting, setFieldValue }) => (
                <Form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6 py-2">
                        {/* Dropdown Pilih Unit */}
                        <div>
                            <label htmlFor="id_unit">Pilih Unit</label>
                            <Field
                                as="select"
                                name="id_unit"
                                className="my-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200"
                            >
                                <option value="">-- Pilih Unit --</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>
                                        {unit.nama_unit} - {unit.plat_nomor}
                                    </option>
                                ))}
                            </Field>
                            <ErrorMessage name="id_unit" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        {/* Harga Harian */}
                        <div>
                            <label htmlFor="harga_harian">Harga Harian</label>
                            <CurrencyInput
                                id="harga_harian"
                                name="harga_harian"
                                value={values.harga_harian}
                                onChange={(value) => {
                                    const salary = value;
                                    setFieldValue('harga_harian', salary)
                                }}
                                currency="IDR"
                                placeholder="Masukkan Harga Harian"
                                className="my-1 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200 text-end"
                            />
                            <ErrorMessage name="harga_harian" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        {/* Harga Mingguan */}
                        <div>
                            <label htmlFor="harga_mingguan">Harga Mingguan</label>
                            <CurrencyInput
                                id="harga_mingguan"
                                name="harga_mingguan"
                                value={values.harga_mingguan}
                                onChange={(value) => {
                                    const salary = value;
                                    setFieldValue('harga_mingguan', salary)
                                }}
                                currency="IDR"
                                placeholder="Masukkan Harga Mingguan"
                                className="my-1 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200 text-end"
                            />
                            <ErrorMessage name="harga_mingguan" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        {/* Harga Bulanan */}
                        <div>
                            <label htmlFor="harga_bulanan">Harga Bulanan</label>
                            <CurrencyInput
                                id="harga_bulanan"
                                name="harga_bulanan"
                                value={values.harga_bulanan}
                                onChange={(value) => {
                                    const salary = value;
                                    setFieldValue('harga_bulanan', salary)
                                }}
                                currency="IDR"
                                placeholder="Masukkan Harga Bulanan"
                                className="my-1 p-2 block w-full border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-200 text-end"
                            />
                            <ErrorMessage name="harga_bulanan" component="div" className="text-red-500 text-sm mt-1" />
                        </div>

                        {id && (
                            <div className="px-6 py-2">
                                <label className="block font-medium text-sm text-gray-700 mb-1">
                                    Ketersediaan Harga
                                </label>
                                <div className="flex items-center space-x-6">
                                    <label className="flex items-center space-x-2">
                                        <Field
                                            type="radio"
                                            name="is_tersedia"
                                            value="tersedia"
                                            className="form-radio text-green-600"
                                        />
                                        <span>Tersedia</span>
                                    </label>
                                    <label className="flex items-center space-x-2">
                                        <Field
                                            type="radio"
                                            name="is_tersedia"
                                            value="tidak tersedia"
                                            className="form-radio text-red-600"
                                        />
                                        <span>Tidak Tersedia</span>
                                    </label>
                                </div>
                            </div>
                        )}


                    </div>

                    {/* Tombol Submit */}
                    <div className="px-6 py-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                        >
                            {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default EntryPrice;
